import Button from "@/common/elements/Button";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import DynamicForm from "@/common/DynamicForm";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { Flex, Form, Popconfirm } from "antd";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import PropertyModal from "./PropertyModal";
import {
  FormContainer,
  InmateDetailsTableContainer,
  TableHeader,
} from "./styles";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs from "dayjs";
import { labels } from "@/util/label";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  debounce,
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { v4 as uuidV4 } from "uuid";
import PropertyListQuickAddForm from "../BookingsDetails/PropertyListQuickAddForm";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID, SCHEMA_NAME, SUB_MODULES_LIST_WITH_ID } from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const PropertyTableHeaders = [
  { key: "inmatePropertyType", text: "Inmate Property Type"  },
  { key: "location",           text: "location"              },
  { key: "Status",             text: "Item Status"           },
  { key: "color",              text: "Item Color"            },
  { key: "quantity",           text: "Item Quantity"         },
  { key: "description",        text: "Summary Description"   },
];

const Property = forwardRef(
  ({ propertyRef, propertyShow, isReleaseAllProperty, startingPoint = "" }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
    const fullSearch = useSelector((s) => s.fullSearch);

    const propertyItemFormRef    = useRef();
    const propertyQuickAddFormRef = useRef();
    const [formOuter] = Form.useForm();

    const [isModalShown, setIsModalShown]         = useState(false);
    const [inmateProperty, setInmateProperty]     = useState(null);
    const [inmatePropertyItems, setInmatePropertyItems] = useState([]);
    const [propertyEditData, setPropertyEditData] = useState(null);
    const [isEdit, setIsEdit]                     = useState(false);

    // -----------------------------------------------------------------------
    // InmatePropertyExtra — dynamic per-client fields (PCI shows these under
    // "Additional Inmate Property Information", right after the property
    // release fields, before the Property Items list). The saved row itself
    // (with its real PK) is fetched once, centrally, by
    // useInmatePropertyExtraSync in BookingsDetails/index.jsx — not here;
    // see hooks/useExtraTableSync.js.
    // -----------------------------------------------------------------------
    const [apiFields, setApiFields] = useState([]);
    // True once fieldHtmlTypeMapRef is populated — gates the effect below so
    // a date/datetime extra column's value is never handed to the form as a
    // plain string (see AdditionalPersonalInformation.jsx for why that
    // matters — antd's DatePicker crashes calling .isValid() on one).
    const [fieldsReady, setFieldsReady] = useState(false);
    // columnName -> htmlType ("date" / "datetime" / ...) for every
    // InmatePropertyExtra field.
    const fieldHtmlTypeMapRef = useRef({});
    // Separate from `inmateProperty` (base fields) so the fieldsReady gate
    // only ever delays the extra columns, never the base property form.
    const [extraFormValues, setExtraFormValues] = useState(null);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/InmatePropertyExtra`)
        .then((response) => {
          const tableData = response?.data?.filter((d) =>
            d?.columnName?.endsWith("_U"),
          );
          const converted = convertApiFields(tableData, "InmatePropertyExtra");

          const typeMap = {};
          converted.forEach((c) => {
            typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
          });
          fieldHtmlTypeMapRef.current = typeMap;

          setApiFields(converted);
          setFieldsReady(true);
        })
        .catch((error) => {
          console.error(
            "Request failed:",
            error.response ? error.response.data : error.message,
          );
        });
    }, []);

    useEffect(() => {
      if (!fieldsReady) return;

      const extra = bookings?.inmateProperty?.[0]?.inmatePropertyExtra?.[0];
      if (!extra) {
        setExtraFormValues(null);
        return;
      }

      const withDates = Object.keys(extra).reduce((acc, key) => {
        const val = extra[key];
        const htmlType = fieldHtmlTypeMapRef.current[key];
        const isDateField = htmlType === "date" || htmlType === "datetime";
        acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
        return acc;
      }, {});
      setExtraFormValues(withDates);
    }, [bookings, fieldsReady]);

    // -----------------------------------------------------------------------
    // Sub-module security — computed once
    // -----------------------------------------------------------------------
    const canAdd    = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.ADD,    MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Inmate_Property);
    const canView   = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.VIEW,   MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Inmate_Property);
    const canDelete = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.DELETE, MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Inmate_Property);

    // "Config" — drag-reorder/resize columns, persisted per-user (see
    // docs/plans/grid-column-config-feature.md).
    const {
      headers: propertyHeaders,
      isConfiguring,
      configButtonLabel,
      startConfiguring,
      confirmSave,
      discardChanges,
      dataTableRef,
    } = useGridColumnConfig("Bookings:InmatePropertyItems", PropertyTableHeaders);

    // -----------------------------------------------------------------------
    // Reference data for location lookups
    // -----------------------------------------------------------------------
    const storageLocation1Options = getOptionsFromRefList("Storage_Location_1",                 false, false, true);
    const storageLocation2Options = getOptionsFromRefList("Inmate_Property_Storage_Location_1", false, false, true);
    const storageLocation3Options = getOptionsFromRefList("Inmate_Property_Storage_Location_2", false, false, true);

    // -----------------------------------------------------------------------
    // Sync bookings → local state
    // -----------------------------------------------------------------------
    useEffect(() => {
      const inmatePropertyCopy = bookings?.inmateProperty?.[0];
      if (bookings !== null) {
        setInmateProperty({
          ...inmatePropertyCopy,
          dateTaken:    inmatePropertyCopy?.dateTaken    ? dayjs(inmatePropertyCopy.dateTaken)    : null,
          dateReleased: !fullSearch
            ? (inmatePropertyCopy?.dateReleased ? dayjs(inmatePropertyCopy.dateReleased) : dayjs())
            : null,
          propertyReleased: inmatePropertyCopy?.propertyReleased,
        });
        setInmatePropertyItems(
          (inmatePropertyCopy?.inmatePropertyItems || []).map((i) => ({ ...i, uuid: uuidV4() }))
        );
      } else {
        setInmateProperty(null);
        setInmatePropertyItems([]);
      }
    }, [bookings]);

    useEffect(() => {
      if (inmateProperty !== null) {
        formOuter.setFieldsValue({
          ...inmateProperty,
          ...extraFormValues,
          takenBy: isNonEmptyVariable(inmateProperty?.takenBy)
            ? parseInt(inmateProperty.takenBy) : null,
        });
      } else {
        formOuter.resetFields();
      }
    }, [inmateProperty, extraFormValues]);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(propertyRef, () => ({
      submitForm:        () => formOuter.submit(),
      resetForm:         () => formOuter.resetFields(),
      getFormValues:     () => formOuter.getFieldsValue(),
      getFieldsError:    () => formOuter.getFieldsError(),
      isFieldsValidating: () => formOuter.isFieldsValidating(),
      validateFields:    () => formOuter.validateFields(),
    }));

    // -----------------------------------------------------------------------
    // Redux sync helpers
    // -----------------------------------------------------------------------
    const updateForm = (data, key = "") => {
      if (key === "table") {
        dispatch(setBookings({
          inmateProperty: [{
            ...inmateProperty,
            propertyComments: inmateProperty?.propertyComments,
            propertyExtras: [{
              ownerAppliedNumber: data.find((p) =>
                p?.ownerAppliedNumber !== null && p?.ownerAppliedNumber !== undefined
              )?.ownerAppliedNumber || "",
              propertyId:    inmateProperty?.inmatePropertyId,
              propertyExtraId: inmateProperty?.propertyExtras?.find(
                (pe) => pe.propertyId === inmateProperty?.inmatePropertyId
              )?.propertyExtraId,
            }],
            dateTaken: isNonEmptyVariable(inmateProperty?.dateTaken)
              ? dayjs(inmateProperty.dateTaken).format("MM/DD/YYYY") : null,
            dateReleased: !fullSearch
              ? (isNonEmptyVariable(inmateProperty?.dateReleased)
                  ? dayjs(inmateProperty.dateReleased).format("MM/DD/YYYY") : dayjs())
              : null,
            propertyReleased: inmateProperty?.itemStatus !== "Disposed" ? false : true,
            inmatePropertyItems: data,
          }],
        }));
      } else {
        // Split incoming form values into base property fields and
        // InmatePropertyExtra dynamic fields (identified via
        // fieldHtmlTypeMapRef, built from the live InmatePropertyExtra
        // field-defs response) — the extra ones are formatted per their SQL
        // type and merged into their own nested inmatePropertyExtra[0]
        // instead of landing on inmateProperty[0] itself.
        const baseData = { ...data };
        const extraFieldsData = {};
        Object.keys(data || {}).forEach((k) => {
          if (!Object.prototype.hasOwnProperty.call(fieldHtmlTypeMapRef.current, k)) {
            return;
          }
          const htmlType = fieldHtmlTypeMapRef.current[k];
          const val = data[k];
          if (val) {
            extraFieldsData[k] =
              htmlType === "date" ? dayjs(val).format("MM/DD/YYYY")
              : htmlType === "datetime" ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val;
          } else {
            extraFieldsData[k] = null;
          }
          delete baseData[k];
        });

        const existingExtra = inmateProperty?.inmatePropertyExtra?.[0] || {};

        dispatch(setBookings({
          inmateProperty: [{
            ...inmateProperty,
            ...baseData,
            propertyExtras: [{
              ownerAppliedNumber: data?.ownerAppliedNumber || "",
              propertyId:         inmateProperty?.inmatePropertyId,
              propertyExtraId:    inmateProperty?.propertyExtras?.find(
                (pe) => pe.propertyId === inmateProperty?.inmatePropertyId
              )?.inmatePropertyId,
            }],
            inmatePropertyExtra: [{
              ...existingExtra,
              ...extraFieldsData,
              inmatePropertyExtraID: existingExtra?.inmatePropertyExtraID ?? 0,
              inmatePropertyID:
                existingExtra?.inmatePropertyID ??
                inmateProperty?.inmatePropertyId ??
                0,
            }],
            propertyComments: data?.propertyComments,
            dateTaken: isNonEmptyVariable(data?.dateTaken)
              ? dayjs(data.dateTaken).format("MM/DD/YYYY") : null,
            propertyReleased: inmateProperty?.propertyReleased,
            dateReleased: !fullSearch
              ? (isNonEmptyVariable(data?.dateReleased)
                  ? dayjs(data.dateReleased).format("MM/DD/YYYY") : dayjs())
              : null,
            propertyPhotos: data[0]?.propertyPhotos
              ? [...(bookings?.inmateProperty?.[0]?.propertyPhotos || []), data[0].propertyPhotos]
              : [],
          }],
        }));
      }
    };

    const dataModify = debounce(() => {
      const values = formOuter.getFieldsValue();
      updateForm({
        ...values,
        dateTaken: isNonEmptyVariable(values?.dateTaken)
          ? dayjs(values.dateTaken).format("MM/DD/YYYY") : null,
        dateReleased: !fullSearch
          ? (isNonEmptyVariable(values?.dateReleased)
              ? dayjs(values.dateReleased).format("MM/DD/YYYY") : dayjs())
          : null,
        propertyComments: values?.propertyComments || inmateProperty?.propertyComments,
      });
    }, 800);

    // -----------------------------------------------------------------------
    // Property item handlers
    // -----------------------------------------------------------------------
    const handleSubmit = () => {
      propertyItemFormRef?.current?.validateFields()
        .then(() => {
          const formValues = propertyItemFormRef.current.getFormValues();
          let data = isNonEmptyVariable(inmatePropertyItems)
            ? JSON.parse(JSON.stringify(inmatePropertyItems))
            : [];

          if (isNonEmptyVariable(propertyEditData) && data?.length) {
            data = data.map((p) =>
              p?.uuid === propertyEditData.uuid ? { ...p, ...formValues } : p
            );
          } else {
            data.push({ ...formValues });
          }
          updateForm(data, "table");
          onCancelClick();
        })
        .catch((err) => console.error("Validation error:", err));
    };

    const onAddClick = () => {
      setPropertyEditData(null);
      const form = propertyItemFormRef?.current?.form;
      setIsModalShown(true);
      form?.resetFields();
      if (form?.getFieldsValue()) {
        Object.keys(form.getFieldsValue()).forEach((k) => form.setFieldValue(k, ""));
      }
    };

    const onCancelClick = () => {
      setPropertyEditData(null);
      setIsModalShown(false);
      setIsEdit(false);
    };

    const onTableRowSelected = (values) => {
      const data = inmatePropertyItems?.find((ip) => ip?.uuid === values?.uuid);
      setPropertyEditData({
        ...data,
        ownerAppliedNumber: inmateProperty?.propertyExtras?.[0]?.ownerAppliedNumber,
        propertyExtraId:    inmateProperty?.propertyExtras?.[0]?.propertyExtraId,
      });
    };

    const onPropertyEditClick = () => { setIsModalShown(true); setIsEdit(true); };

    const onPropertyDeleteClick = () => {
      let data = JSON.parse(JSON.stringify(inmatePropertyItems))
        .filter((d) => d?.uuid !== propertyEditData?.uuid);

      if (propertyEditData?.inmatePropertyItemId) {
        dispatch(setDeleteAPis(API_ENDPOINTS.inmateProperty + propertyEditData.inmatePropertyItemId));
      }
      dispatch(setBookings({
        inmateProperty: [{ ...bookings?.inmateProperty?.[0], inmatePropertyItems: data }],
      }));
      onCancelClick();
    };

    const handleQuickAdd = () => {
      if (!propertyQuickAddFormRef.current) return;
      propertyQuickAddFormRef.current.validateFields()
        .then((values) => {
          if (!isNonEmptyVariable(values)) return;

          const defaultLoc = 50500006;
          const loc2Value  = values?.inventoryItems?.[0]?.storageLocation2Id || "";
          const loc3Value  = values?.inventoryItems?.[0]?.storageLocation3Id || "";

          const loc1Opts = getOptionsFromRefList("Storage_Location_1",                 false, false, true);
          const loc2Opts = getOptionsFromRefList("Inmate_Property_Storage_Location_1", false, false, true);
          const loc3Opts = getOptionsFromRefList("Inmate_Property_Storage_Location_2", false, false, true);

          const loc1Name = loc1Opts.find((o) => o.value === defaultLoc)?.label || defaultLoc;
          const loc2Name = loc2Opts.find((o) => o.value === loc2Value)?.label  || loc2Value;
          const loc3Name = loc3Opts.find((o) => o.value === loc3Value)?.label  || loc3Value;

          const locationComment = `ORIGINAL STORAGE: ${loc1Name}:${loc2Name}:${loc3Name}`;
          const initialCustody  = {
            inventoryItemTransactionId: null,
            dateAdded: !fullSearch ? dayjs().format("MM/DD/YYYY") : null,
            timeAdded: !fullSearch ? dayjs().format("HH:mm")      : null,
            user:              "System",
            transactionType:   "Initial Entry",
            comments: fullSearch
              ? ((loc1Name || loc2Name || loc3Name) ? locationComment : null)
              : locationComment,
            inventoryItemTransactionsExtras: [],
          };

          const newPropertyItem = {
            ...values,
            uuid:                    uuidV4(),
            inventoryItemTransactions: [initialCustody],
            itemStatus:              "Checked In",
          };

          setInmatePropertyItems((prevItems = []) => {
            if (isNonEmptyVariable(newPropertyItem.inmatePropertyType)) {
              const updated = [...prevItems, newPropertyItem];
              updateForm(updated, "table");
              return updated;
            }
            return prevItems;
          });

          propertyQuickAddFormRef.current.resetForm();
        })
        .catch((err) => console.error("Validation failed:", err));
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <>
        <FieldSecurityProvider
          entity={SCHEMA_NAME.INMATE_PROPERTY}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          {/* ── Property Header Form (shown when propertyShow=true) ──── */}
          {propertyShow && (
            <FormContainer>
              <Form
                form={formOuter}
                initialValues={inmateProperty}
                disabled={!bookingsFieldsEnabled}
                onFieldsChange={dataModify}
              >
                <StyledGridRow gutter={[16]}>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="searchOfficerStaffId"
                      formItemProps={{ name: "searchOfficerStaffId", label: labels.searchedBy }}
                    >
                      <Select showSearch options={getOptionsFromRefList("", true, false, true)} />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="searchTechniqueId"
                      formItemProps={{ name: "searchTechniqueId", label: labels.searchTechnique }}
                    >
                      <Select options={getOptionsFromRefList("Search_Technique", false, false, true)} showSearch />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="takenBy"
                      formItemProps={{ name: "takenBy", label: labels.takenBy }}
                    >
                      <Select showSearch options={getOptionsFromRefList("", true)} />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="releasedTo"
                      formItemProps={{ name: "releasedTo", label: labels.releasedTo }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="dateTaken"
                      formItemProps={{ name: "dateTaken", label: labels.propertyDate }}
                    >
                      <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="cash"
                      formItemProps={{ name: "cash", label: labels.cash }}
                    >
                      <TextField
                        onKeyDown={(e) => {
                          if (
                            e.key.match(/[^0-9.]/) &&
                            !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
                          ) e.preventDefault();
                        }}
                      />
                    </SecureFormItem>
                  </StyledGridCol>

                </StyledGridRow>

                {/* Release fields — only when releasing all property */}
                {(isReleaseAllProperty || inmateProperty?.propertyReleased) && (
                  <StyledGridRow gutter={[16]}>

                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="releasedTo"
                        formItemProps={{
                          name: "releasedTo", label: "Actually Released To",
                          rules: [{ required: true, message: "Please enter Actually Released To" }],
                        }}
                      >
                        <TextField />
                      </SecureFormItem>
                    </StyledGridCol>

                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="releasedByStaffId"
                        formItemProps={{
                          name: "releasedByStaffId", label: "Released By",
                          rules: [{ required: true, message: "Please enter Released By" }],
                        }}
                      >
                        <Select showSearch options={getOptionsFromRefList("", true)} />
                      </SecureFormItem>
                    </StyledGridCol>

                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="dateReleased"
                        formItemProps={{
                          name: "dateReleased", label: "Release Date",
                          initialValue: dayjs(),
                          rules: [{ required: true, message: "Please select Release Date" }],
                        }}
                      >
                        <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} />
                      </SecureFormItem>
                    </StyledGridCol>

                  </StyledGridRow>
                )}

                {/* Dynamic extra fields — InmatePropertyExtra. PCI shows
                    these under "Additional Inmate Property Information",
                    right after the property release fields above. */}
                {apiFields.length > 0 && (
                  <StyledGridRow gutter={[16]}>
                    {apiFields.map((sf, index) => {
                      const view = hasCheckSecurity(
                        "Functionality", "field", "VIEW", "", "",
                        "InmatePropertyExtra", sf.columnName, startingPoint,
                      );
                      const edit = hasCheckSecurity(
                        "Functionality", "field", "MODIFY", "", "",
                        "InmatePropertyExtra", sf.columnName, startingPoint,
                      );
                      return (
                        <DynamicForm
                          key={index}
                          formAttributes={sf}
                          form={formOuter}
                          bookingsFieldsEnabled={bookingsFieldsEnabled}
                          fullSearch={fullSearch}
                          view={view}
                          edit={edit}
                        />
                      );
                    })}
                  </StyledGridRow>
                )}
              </Form>
            </FormContainer>
          )}

          {/* ── Property Items Table (shown when propertyShow=false) ─── */}
          {!propertyShow && (
            <>
              <PropertyListQuickAddForm
                bookingsFieldsEnabled={bookingsFieldsEnabled}
                ref={propertyQuickAddFormRef}
                onQuickAdd={handleQuickAdd}
                startingPoint={startingPoint}
              />

              <InmateDetailsTableContainer>
                <TableHeader>
                  <h6>List of items taken</h6>
                  <Flex gap={4}>

                    <Button color="primary" type="primary" className="add-btn"
                      onClick={onAddClick} icon={<PlusOutlined />}
                      disabled={!bookingsFieldsEnabled || !canAdd || isConfiguring}
                    >
                      Add
                    </Button>

                    <Button color="primary" type="primary" className="add-btn"
                      onClick={onPropertyEditClick} icon={<EditOutlined />}
                      disabled={propertyEditData === null || !canView || isConfiguring}
                    >
                      Details
                    </Button>

                    <Popconfirm
                      title="Delete the property item"
                      description="Are you sure to delete this property item?"
                      onConfirm={onPropertyDeleteClick}
                      onCancel={() => {}}
                      okText="Yes" cancelText="No"
                    >
                      <Button color="primary" type="primary" className="add-btn"
                        icon={<DeleteOutlined />}
                        disabled={!bookingsFieldsEnabled || propertyEditData === null || !canDelete || isConfiguring}
                      >
                        Delete
                      </Button>
                    </Popconfirm>

                    <Button color="primary" type="primary" className="add-btn"
                      onClick={onCancelClick} icon={<StopOutlined />}
                      disabled={propertyEditData === null || isConfiguring}
                    >
                      Cancel
                    </Button>

                    {/* Config — drag-reorder/resize columns */}
                    {!isConfiguring ? (
                      <Button color="primary" type="primary" className="add-btn"
                        htmlType="button" onClick={startConfiguring}
                      >
                        {configButtonLabel}
                      </Button>
                    ) : (
                      <Popconfirm
                        title="Do you want to save the current list view configuration?"
                        onConfirm={confirmSave}
                        onCancel={discardChanges}
                        okText="Yes" cancelText="No"
                      >
                        <Button color="primary" type="primary" className="add-btn" htmlType="button">
                          {configButtonLabel}
                        </Button>
                      </Popconfirm>
                    )}

                  </Flex>
                </TableHeader>

                {canView && (
                  <Table
                    ref={dataTableRef}
                    headers={propertyHeaders}
                    draggable={isConfiguring}
                    data={(inmatePropertyItems || []).map((i) => {
                      const loc1Id = i?.inventoryItems?.[0]?.storageLocation1Id;
                      const loc2Id = i?.inventoryItems?.[0]?.storageLocation2Id;
                      const loc3Id = i?.inventoryItems?.[0]?.storageLocation3Id;
                      const loc1Name = storageLocation1Options.find((o) => o.value === loc1Id)?.label || "";
                      const loc2Name = storageLocation2Options.find((o) => o.value === loc2Id)?.label || "";
                      const loc3Name = storageLocation3Options.find((o) => o.value === loc3Id)?.label || "";
                      return {
                        inmatePropertyId:   i?.inmatePropertyId,
                        inmatePropertyType: i?.inmatePropertyType || "",
                        uuid:               i?.uuid,
                        location:           `${loc1Name} : ${loc2Name} : ${loc3Name}`,
                        Status:             i?.inventoryItems?.[0]?.itemStatus || "Checked In",
                        color:              i?.inventoryItems?.[0]?.propertyCommon?.[0]?.color    || "",
                        quantity:           i?.inventoryItems?.[0]?.propertyCommon?.[0]?.quantity || "",
                        description: `${i?.inventoryItems?.[0]?.propertyCommon?.[0]?.description || ""} ${i?.inventoryItems?.[0]?.propertyWithSerialNumber?.[0]?.propertyWithSerialNumberId || ""}`.trim(),
                      };
                    })}
                    onRowClick={onTableRowSelected}
                    onRowDoubleClick={(data) => { onTableRowSelected(data); onPropertyEditClick(); }}
                    selected={propertyEditData === null}
                  />
                )}

                {/* Comments form */}
                <StyledGridCol lg={24}>
                  <Form form={formOuter} onFieldsChange={dataModify}>
                    <SecureFormItem
                      fieldName="propertyComments"
                      formItemProps={{ name: "propertyComments", label: "Comments" }}
                    >
                      <TextField inputType="textarea" rows="4" />
                    </SecureFormItem>
                  </Form>
                </StyledGridCol>
              </InmateDetailsTableContainer>

              {isModalShown && (
                <PropertyModal
                  show={isModalShown}
                  propertyItemFormRef={propertyItemFormRef}
                  property={propertyEditData}
                  handleCancel={onCancelClick}
                  handleSubmit={handleSubmit}
                  isEdit={isEdit}
                  startingPoint={startingPoint}
                />
              )}
            </>
          )}
        </FieldSecurityProvider>
      </>
    );
  }
);

export default Property;